import { MODULE_CONTRACT_VERSION, type ModuleContribution, type OchalModuleManifest, type PluginCatalog } from '@ochal/module-sdk/contracts';
import { parseVersion } from './semver';

export class ManifestValidationError extends Error {
  constructor(public readonly issues: readonly string[]) { super(`Invalid module manifest: ${issues.join('; ')}`); this.name = 'ManifestValidationError'; }
}
const idPattern = /^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)*$/;
const contributionKinds = new Set(['route', 'navigation', 'dashboard-widget', 'settings-section', 'component', 'query', 'action', 'notification-handler']);
function validateContribution(value: ModuleContribution, index: number, issues: string[]) {
  if (!idPattern.test(value.id)) issues.push(`contributions[${index}].id is invalid`);
  if (typeof value.load !== 'function') issues.push(`contributions[${index}].load must be a function`);
  if (!contributionKinds.has(value.kind)) issues.push(`contributions[${index}].kind is invalid`);
  if (value.order !== undefined && (!Number.isInteger(value.order) || value.order < 0)) issues.push(`contributions[${index}].order is invalid`);
  if (value.permission && (!idPattern.test(value.permission) || !value.permission.includes('.'))) issues.push(`contributions[${index}].permission is invalid`);
  if (value.kind === 'route') {
    const route = value as Extract<ModuleContribution, { kind: 'route' }>;
    if (!route.path?.startsWith('/') || !route.title?.trim()) issues.push(`contributions[${index}] route metadata is invalid`);
  }
  if (value.kind === 'dashboard-widget' || value.kind === 'settings-section') {
    const titled = value as Extract<ModuleContribution, { kind: 'dashboard-widget' | 'settings-section' }>;
    if (!titled.title?.trim()) issues.push(`contributions[${index}].title is required`);
  }
}
export function validateManifest(value: unknown): OchalModuleManifest {
  const issues: string[] = [];
  if (!value || typeof value !== 'object') throw new ManifestValidationError(['manifest must be an object']);
  const manifest = value as Partial<OchalModuleManifest>;
  if (manifest.contractVersion !== MODULE_CONTRACT_VERSION) issues.push(`contractVersion must be ${MODULE_CONTRACT_VERSION}`);
  if (!manifest.id || !idPattern.test(manifest.id)) issues.push('id is invalid');
  if (!manifest.name?.trim()) issues.push('name is required');
  if (!manifest.version || !parseVersion(manifest.version)) issues.push('version must be semantic');
  if (typeof manifest.enabled !== 'boolean') issues.push('enabled must be boolean');
  if (!manifest.sdkVersion?.trim()) issues.push('sdkVersion is required');
  if (!manifest.runtimeVersion?.trim()) issues.push('runtimeVersion is required');
  const contributionIds = new Set<string>();
  manifest.contributions?.forEach((contribution, index) => {
    validateContribution(contribution, index, issues);
    if (contributionIds.has(contribution.id)) issues.push(`duplicate contribution id ${contribution.id}`);
    contributionIds.add(contribution.id);
  });
  const permissionCodes = new Set<string>();
  manifest.permissions?.forEach((permission, index) => {
    if (!idPattern.test(permission.code) || !permission.code.includes('.')) issues.push(`permissions[${index}].code is invalid`);
    if (permissionCodes.has(permission.code)) issues.push(`duplicate permission ${permission.code}`);
    permissionCodes.add(permission.code);
  });
  const dependencyIds = new Set<string>();
  manifest.dependencies?.forEach((dependency, index) => {
    if (!idPattern.test(dependency.id) || !dependency.version.trim()) issues.push(`dependencies[${index}] is invalid`);
    if (dependency.id === manifest.id) issues.push('module cannot depend on itself');
    if (dependencyIds.has(dependency.id)) issues.push(`duplicate dependency ${dependency.id}`);
    dependencyIds.add(dependency.id);
  });
  const serviceIds = new Set<string>();
  manifest.services?.forEach((service, index) => {
    if (!service.token?.id || typeof service.factory !== 'function') issues.push(`services[${index}] is invalid`);
    if (serviceIds.has(service.token?.id)) issues.push(`duplicate service ${service.token?.id}`);
    serviceIds.add(service.token?.id);
  });
  if (issues.length) throw new ManifestValidationError(issues);
  return manifest as OchalModuleManifest;
}
export function validateCatalog(catalog: PluginCatalog): void {
  const issues: string[] = []; const ids = new Set<string>();
  for (const [index, entry] of catalog.entries.entries()) {
    if (!idPattern.test(entry.id)) issues.push(`catalog[${index}].id is invalid`);
    if (typeof entry.load !== 'function') issues.push(`catalog[${index}].load must be a function`);
    if (ids.has(entry.id)) issues.push(`duplicate catalog id ${entry.id}`);
    ids.add(entry.id);
  }
  if (issues.length) throw new ManifestValidationError(issues);
}
